import type { DocNode } from '@atlaskit/adf-schema';
import type { SelectionContext } from '@atlaskit/editor-common/types';
import type { JSONDocNode, JSONNode } from '@atlaskit/editor-json-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { PageContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import type { CommandProperties } from '@atlassian/rovo-content-bridge-api/command';

import { log } from '../utils/aifc-logging';

import { CommandHandler } from './command-handler';
import type { PassThroughCommandProps } from './command-handler';

const isMediaNode = (node?: JSONNode) => {
	if (!node) return false;
	if (node.type === 'media') {
		return typeof (node.attrs as { id?: string })?.id === 'string';
	}
	if (node.type === 'mediaSingle' && Array.isArray(node.content) && node.content.length > 0) {
		const first = node.content[0] as JSONNode;
		return first?.type === 'media' && typeof (first.attrs as { id?: string })?.id === 'string';
	}
	return false;
};

const isMediaNodeSelected = (selection?: SelectionContext) => {
	return selection?.localIds?.length === 1 && isMediaNode(selection?.selectionFragment?.at(0));
};

/**
 * Extracts the media file ID from the selected ADF fragment when the selection is a media node.
 */
const extractMediaFileId = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}
	const first = selectionFragment[0];
	if (!first) return undefined;
	if (first.type === 'media') {
		return (first.attrs as { id?: string })?.id;
	}
	if (first.type === 'mediaSingle' && Array.isArray(first.content) && first.content[0]) {
		const media = first.content[0] as JSONNode;
		return media?.type === 'media' ? (media.attrs as { id?: string })?.id : undefined;
	}
	return undefined;
};

const isMauiAppNode = (node?: JSONNode) => {
	return (
		node?.type === 'extension' &&
		(node.attrs as { extensionKey?: string })?.extensionKey === 'native-embed:maui'
	);
};

const isMauiAppNodeSelected = (selection?: SelectionContext) => {
	return selection?.localIds?.length === 1 && isMauiAppNode(selection?.selectionFragment?.at(0));
};

/**
 * Extracts the Maui content ID from the selected ADF fragment.
 */
const extractMauiContentId = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}

	const mauiApp = selectionFragment.find((node) => isMauiAppNode(node));
	const attrs = mauiApp?.attrs as
		| { extensionKey?: string; parameters?: { macroParams?: { url?: { value?: string } } } }
		| undefined;

	const url: string | undefined = attrs?.parameters?.macroParams?.url?.value;
	if (url) {
		// We only want the ID, which is the part after /remix/ui/
		const match = new RegExp('remix\\/ui\\/([^/?#]+)', 'u').exec(url);
		return match?.[1];
	}
	return undefined;
};

export const buildContentContext = async ({
	props,
	doc,
	selectionFragment,
	selectionLocalIds,
	selectionMarkdown,
	contentMauiId,
	dynamicUiSource,
	dynamicUiType,
	mediaFileId,
	startIndex,
	endIndex,
}: {
	contentMauiId?: string;
	doc: DocNode | JSONDocNode | undefined;
	dynamicUiSource?: PageContentCommandResult['dynamicUiSource'];
	dynamicUiType?: string;
	endIndex?: number;
	mediaFileId?: string;
	props: PassThroughCommandProps;
	selectionFragment: string;
	selectionLocalIds: string;
	selectionMarkdown: string;
	startIndex?: number;
}): Promise<PageContentCommandResult> => {
	let additionalContextFromProps = props.additionalContext || { isViewMode: false };
	if (typeof additionalContextFromProps === 'function') {
		try {
			additionalContextFromProps = await additionalContextFromProps();
			// eslint-disable-next-line no-unused-vars
		} catch (err) {
			additionalContextFromProps = {};
		}
	}
	const {
		isViewMode = false,
		isDraftLockedForEditing = false,
		...additionalContext
	} = additionalContextFromProps || {};

	return {
		contentId: props.contentId,
		contentType: props.contentType,
		contentMauiId,
		dynamicUiSource,
		dynamicUiType,
		mediaFileId,
		pageBody: doc,
		selectionFragment,
		selectionLocalIds,
		selectionMarkdown,
		startIndex,
		endIndex,
		isViewMode,
		isDraftLockedForEditing,
		useGenericEditorSkill: props.useGenericEditorSkill,
		additionalContext: Object.keys(additionalContext).length > 0 ? additionalContext : undefined,
	};
};

export class ReadContentCommandHandler extends CommandHandler<
	CommandProperties,
	PageContentCommandResult
> {
	async execute(): Promise<PageContentCommandResult> {
		const defaultResult: PageContentCommandResult = await buildContentContext({
			props: this.props,
			doc: undefined,
			selectionFragment: '',
			selectionLocalIds: '',
			selectionMarkdown: '',
		});

		try {
			const doc = fg('platform_editor_aifc_rovo_bridge_partial_doc')
				? this.props.editorAPI?.aiExperience?.actions.getPartialDocumentFromSelection()
				: await this.props.editorAPI.core.actions.requestDocumentAsync();
			if (!doc) {
				// TODO: EDITOR-123 - report this error
				return defaultResult;
			}

			const selection = this.props.editorAPI.aiExperience?.actions.getAISelectionContext();
			const selectionFragment = selection?.selectionFragment
				? JSON.stringify(selection.selectionFragment)
				: '';
			const content: PageContentCommandResult = await buildContentContext({
				props: this.props,
				doc,
				selectionFragment,
				startIndex: selection?.startIndex ?? undefined,
				endIndex: selection?.endIndex ?? undefined,
				selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
				selectionMarkdown: selection?.selectionMarkdown ?? '',
				...(expValEquals('cc-maui-experiment', 'isEnabled', true)
					? {
							...(isMauiAppNodeSelected(selection)
								? { contentMauiId: extractMauiContentId(selection?.selectionFragment) }
								: {}),
							...(isMediaNodeSelected(selection)
								? {
										mediaFileId: extractMediaFileId(selection?.selectionFragment),
										dynamicUiType: 'infographic',
									}
								: {}),
							dynamicUiSource: {
								contentType: 'page',
								startLocalId: selection?.localIds?.[0],
								endLocalId: selection?.localIds?.[selection.localIds.length - 1],
								startIndex: selection?.startIndex ?? undefined,
								endIndex: selection?.endIndex ?? undefined,
								fragmentAdf: selectionFragment,
							},
						}
					: {}),
			});

			log('handle read-content-command result', {
				additionalContext: this.props.additionalContext,
				doc,
				selection,
				content,
			});

			return content ?? defaultResult;
		} catch {
			// TODO: EDITOR-123 - report this error
			return defaultResult;
		}
	}
}
